import React, { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { groupBy,  find, isObject } from "lodash";
import { parse, getTime } from 'date-fns';
import _this from "../constants";
import "./index.css";

const LearningGrowthColumn = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    // GET DATA FOR THE CHART:
    const data = [];
    const dataRange = [];
    const dataGroup = groupBy(dataSet.datasets, "id");

    Object.keys(dataGroup).forEach((item1) => {
      // Construct the data chart.

      let dataTemp = dataGroup[item1]
        .map((item2) => {
          item2.category = `${item2.id}/${item2.date}`;
          data.push(item2);
          return item2;
        })
        .sort((a, b) => {
          a = getTime(parse(a.date, "MM/dd/yyyy", new Date()));
          b = getTime(parse(b.date, "MM/dd/yyyy", new Date()));
          return a - b;
        });
      // Construct the data range.
      dataRange.push({
        category: dataTemp[0],
        endCategory: dataTemp[dataTemp.length - 1],
      });
    });

    // Get global constants:
    const {
      FONT_SIZE_AXIS,
      MIN_GRID_VALUE,
      MARGIN_CHART_CONTAINER,
    } = _this;

    MARGIN_CHART_CONTAINER.paddingTop = 10;
    MARGIN_CHART_CONTAINER.paddingBottom = 5;
    MARGIN_CHART_CONTAINER.paddingRight = 5;

    // Set root instance.
    let root = am5.Root.new(chartID);

    // Set themes.
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart.
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
        ...MARGIN_CHART_CONTAINER,
        
      })
    );

    // Remove logo.
    root._logo.dispose();

    // Add scrollbar.
    chart.set("scrollbarX", am5.Scrollbar.new(root, {
      orientation: "horizontal",
      visible: true,
      start: 0,
      end: 0.2,
    }));

    // Create axes.
    let xAxisBotton = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "date",
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: MIN_GRID_VALUE,
      }),
    }));

    let xAxisTop = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "category",
      opposite: true,
      renderer: am5xy.AxisRendererX.new(root, {
        opposite: true,
        minGridDistance: MIN_GRID_VALUE,
      }),
    }));  

    let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      min: 0,
      strictMinMax: true,
      maxPrecision: 0,
      renderer: am5xy.AxisRendererY.new(root, {})
    }));
    
    
    yAxis.children.moveValue(
      am5.Label.new(root, {
        text: "Level of competence",
        fontSize: FONT_SIZE_AXIS,
        fontWeight: "bold",
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50
      }), 0
    );
    yAxis.data.setAll(dataSet.levels);

    xAxisBotton.get("renderer").labels.template.setAll({
      oversizedBehavior: "wrap",
      maxWidth: 100,
      textAlign: "center",
      fontSize: FONT_SIZE_AXIS - 1,
      fontWeight: "normal",
    });

    xAxisTop.get("renderer").labels.template.setAll({
      oversizedBehavior: "wrap",
      maxWidth: 200,
      textAlign: "center",
      fontSize: FONT_SIZE_AXIS,
      fontWeight: "normal",
    });

    yAxis.get("renderer").labels.template.setAll({
      textAlign: "right",
      fontSize: FONT_SIZE_AXIS,
      fontWeight: "normal",
    });

    xAxisTop.get("renderer").labels.template.adapters.add("text", (category, target) => {
      return (category !== "{category}" ? category : "" );
    });

    xAxisBotton.get("renderer").labels.template.adapters.add("text", (date, target) => {
      return "{date.formatDate('MMM, yyyy')}";
    });

    xAxisTop.data.setAll(data);
    xAxisBotton.data.setAll(data);

    // Remove Y grid line.
    xAxisTop.get("renderer").grid.template.set("forceHidden", true);
    xAxisTop.get("renderer").grid.template.set("forceHidden", true);

    dataRange.forEach((item, idx, array) => {
      let range = xAxisTop.makeDataItem({});
      xAxisTop.createAxisRange(range);

      range.set("category", item.category.category);
      range.set("endCategory", item.endCategory.category);

      let label = range.get("label");
      label.setAll({
        text: `MAIN\n\n${item.endCategory.element}\n${item.endCategory.subElement}\n${item.endCategory.organisingElements}\n${item.endCategory.skills}`,
        dy: -10,
        fontWeight: "bold",
        tooltipText: item.endCategory.levelDefinition,
      });

      let tick = range.get("tick");
      tick.setAll({
        visible: true,
        strokeOpacity: 1,
        length: -1000,
        dy: -50,
        location: 0
      });

      let grid = range.get("grid");
      grid.setAll({ strokeOpacity: 1 });

      // add range for the last grid.
      if (idx === array.length - 1) {
        let lastRange = xAxisTop.makeDataItem({});
        xAxisTop.createAxisRange(lastRange);
        lastRange.set("category", item.endCategory.category);
        let lastTick = lastRange.get("tick");
        lastTick.setAll({
          visible: true,
          strokeOpacity: 1,
          length: -1000,
          dy: -50,
          location: 1
        });
        let lastGrid = lastRange.get("grid");
        lastGrid.setAll({
          strokeOpacity: 1,
          location: 1
        });
        let lastLabel = lastRange.get("label");
        lastLabel.setAll({
          visible: false,
        });
      };

    });

    yAxis.get("renderer").labels.template.adapters.add("text", (value) => {
      const label = find(dataSet.levels, { 'value': Number(value) })
      return isObject(label) ? label.name : "-";
    });

    // Add series.
    const createSeries = (d, categoryY, categoryX, name) => {
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: name,
        xAxis: xAxisTop,
        yAxis: yAxis,
        valueYField: categoryY,
        categoryXField: categoryX,
      }));

      let tooltip = am5.Tooltip.new(root, {
        pointerOrientation: "left",
        autoTextColor: false,
        wheelable: false,
        labelText: `{element}\n{subElement}\n{skills}\n{organisingElements}\n ------
[bold]Level Of Competence:[/] {levelName} \n[bold]Descriptor:[/] {levelDefinition}\n[bold]Marking:[/] {markingTitle}\n[bold]Marked By:[/] {markingBy} \n[bold]Marking date:[/] {markingDate.formatDate('dd-MMM-yyyy')} \n[bold]Marking Month:[/] {date.formatDate('MMM, yyyy')}`,
      });

      tooltip.label.setAll({
        textAlign: "left",
        fontSize: 12,
      });

      series.set("tooltip", tooltip);

      // Add styles in column series.
      series.columns.template.setAll({
        tooltipY: 0,
        fillOpacity: 0.8,
        strokeWidth: 1,
        templateField: "columnSettings",
      });

      series.data.processor = am5.DataProcessor.new(root, {
        numericFields: ["value"],
      });

      series.data.setAll(d);
      series.appear();

    };

    // Create series.
    createSeries(data, "value", "category", "Learning growth Column");

    // Add cursor.
    let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));

    // Hide cursor line.
    cursor.lineY.set("forceHidden", true);
    cursor.lineX.set("forceHidden", true);

    // Make stuff animate on load.
    chart.appear(1000, 100);

    // Return before dom render.
    return () => root.dispose();
  }, [chartID, dataSet ]);

  return (
    <div id={chartID} className="col-chart-lgc"></div>
  );
};

// Add PropTypes validation.
LearningGrowthColumn.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired,
};

export default LearningGrowthColumn;
